import {
  useToast,
  Flex,
  FormControl,
  Input,
  useDisclosure,
  Checkbox,
  Text,
} from '@chakra-ui/react';
import {
  Contact,
  ContactAddToType,
  contactsApi,
  CreateContactRequest,
} from '@texas/api/endpoints/contactsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { contactEvents } from './events';
import { SelectCustomerUserModal } from './SelectCustomerUserModal';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { EmptyDataButton } from '../shared/button/EmptyDataButton';
import { UserAvatar } from '../shared/users/UserAvatar';
import { Icons } from '../shared/Icons';
import { SimpleUserAvatarButtons } from '../shared/users/SimpleUserAvatarButtons';
import { CustomerUser } from '@texas/api/endpoints/customerUsersApi';
import { Role } from '@texas/api/endpoints/userApi';
import { userEvents } from '../users/events';
import { RoleRestrict } from '../shared/RoleRestrict';
import { FeatureGuard } from '../shared/featureGuard/FeatureGuard';

export function CreateContact({
  onCreated,
  referenceId,
  addTo,
  input,
}: {
  onCreated: (c: Contact) => void;
  referenceId: number;
  addTo: ContactAddToType;
  input: string;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    isOpen: isSelectUserOpen,
    onClose: onSelectUserClose,
    onOpen: onSelectUserOpen,
  } = useDisclosure();
  const [createLogin, setCreateLogin] = useState(false);
  const [sendWelcomeMail, setSendWelcomeMail] = useState(false);
  const { request: createWithLogin, error: loginError } = useApiRequest(
    contactsApi.createWithLogin,
  );
  const [selectedUser, setSelectedUser] = useState<CustomerUser>();
  const { request: createRequest, error } = useApiRequest(contactsApi.create);

  const defaultValues: CreateContactRequest = {
    referenceId,
    name: '',
    email: '',
    userId: undefined,
    addTo,
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateContactRequest>({ defaultValues });

  const created = (c: Contact, welcomeEmailFailed = false) => {
    reset(defaultValues);
    toast({
      title: t('general.created'),
      status: 'success',
      isClosable: true,
    });
    if (welcomeEmailFailed)
      toast({
        title: t('user.welcomeEmailFailed'),
        status: 'warning',
        isClosable: true,
        duration: null,
      });
    onCreated(c);
    contactEvents.newContactCreated.dispatch();
    if (createLogin) userEvents.newUserCreated.dispatch();
  };

  const onSubmit = async (data: CreateContactRequest) => {
    if (createLogin) {
      await request(
        createWithLogin,
        [{ ...data, userId: undefined, sendWelcomeMail }],
        (result) => created(result.contact, result.welcomeEmailFailed),
      );
    } else {
      await request(createRequest, [data], (c) => created(c));
    }
  };

  useEffect(() => {
    const isEmail = input.includes('@');
    setValue('name', !isEmail ? input : '');
    setValue('email', isEmail ? input : '');
  }, [input, setValue]);

  return (
    <>
      {error && <ErrorDetails error={error} />}
      {loginError && <ErrorDetails error={loginError} />}
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Flex direction="column" gap={4}>
          <FormControl isRequired={true} isInvalid={!!errors.name}>
            <TexasFormLabel>{t('general.name')}</TexasFormLabel>
            <Input
              variant="texas"
              {...register('name', {
                required: true,
              })}
              placeholder={t('general.name')}
            />
            <ErrorLabel text={errors.name?.message} />
          </FormControl>
          <FormControl isRequired={true} isInvalid={!!errors.email}>
            <TexasFormLabel>{t('general.email')}</TexasFormLabel>
            <Input
              type="email"
              variant="texas"
              {...register('email', {
                required: true,
              })}
              placeholder={t('general.email')}
            />
            <ErrorLabel text={errors.email?.message} />
          </FormControl>
          <RoleRestrict allow={[Role.SystemAdministrator]}>
            <Checkbox
              isChecked={createLogin}
              onChange={(event) => {
                setCreateLogin(event.target.checked);
                setValue('userId', undefined);
                setSelectedUser(undefined);
                setSendWelcomeMail(false);
              }}
            >
              {t('contacts.createLogin')}
            </Checkbox>
            {createLogin && (
              <>
                <Text fontSize="sm">
                  {t('contacts.createLoginDescription')}
                </Text>
                <Checkbox
                  isChecked={sendWelcomeMail}
                  onChange={(event) => setSendWelcomeMail(event.target.checked)}
                >
                  {t('user.sendWelcomeEmail')}
                </Checkbox>
              </>
            )}
          </RoleRestrict>
          {!createLogin &&
            compareEnum(ContactAddToType, addTo, ContactAddToType.Customer) && (
              <FeatureGuard>
                {selectedUser ? (
                  <UserAvatar
                    name={selectedUser.userName}
                    email={selectedUser.email}
                    label={null}
                    showTeams={false}
                    buttons={
                      <SimpleUserAvatarButtons
                        buttons={[
                          {
                            tooltip: t('customerUser.change'),
                            icon: <Icons.Pencil />,
                            onClick: onSelectUserOpen,
                          },
                          {
                            tooltip: t('general.remove'),
                            icon: <Icons.Close />,
                            onClick: () => {
                              setValue('userId', undefined);
                              setSelectedUser(undefined);
                            },
                          },
                        ]}
                      />
                    }
                  />
                ) : (
                  <EmptyDataButton
                    label={t('customerUser.select')}
                    description={t('customerUser.change')}
                    onClick={onSelectUserOpen}
                  />
                )}
              </FeatureGuard>
            )}
          <SubmitButton loading={isSubmitting}>
            {t('general.create')}
          </SubmitButton>
        </Flex>
      </form>
      {compareEnum(ContactAddToType, addTo, ContactAddToType.Customer) && (
        <SelectCustomerUserModal
          customerId={referenceId}
          isOpen={isSelectUserOpen}
          onClose={onSelectUserClose}
          onSelect={(user) => {
            setValue('userId', user.userId);
            setSelectedUser(user);
          }}
        />
      )}
    </>
  );
}
